import React from 'react';
import { useTranslation } from 'react-i18next';
import { BillToDraft } from '../invoiceForm';
import catalog from '../../catalog/Catalog.module.css';
import styles from '../Invoice.module.css';

const MAX_BILL_TO_EMAILS = 10;

type InvoiceBillToEmailsProps = {
  emails: string[];
  disabled?: boolean;
  sendAction?: React.ReactNode;
  onChange: (emails: string[]) => void;
};

export const InvoiceBillToEmails: React.FC<InvoiceBillToEmailsProps> = ({
  emails,
  disabled,
  sendAction,
  onChange,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  const rows = emails.length > 0 ? emails : [''];

  return (
    <div className={catalog.field}>
      <div className={styles.emailFieldHead}>
        <label className={catalog.label}>{t('billToEmail', { ns: 'invoices' })}</label>
        {sendAction}
      </div>
      {rows.map((email, index) => (
        <div key={index} className={styles.emailRow}>
          <input
            className={catalog.input}
            type="email"
            value={email}
            disabled={disabled}
            onChange={event => onChange(rows.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)))}
          />
          {rows.length > 1 && !disabled ? (
            <button
              className={styles.iconButton}
              type="button"
              aria-label={t('removeEmail', { ns: 'invoices' })}
              onClick={() => onChange(rows.filter((_, itemIndex) => itemIndex !== index))}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      {!disabled && rows.length < MAX_BILL_TO_EMAILS ? (
        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => onChange([...rows, ''])}
        >
          {t('addEmail', { ns: 'invoices' })}
        </button>
      ) : null}
    </div>
  );
};

type InvoiceBillToFieldsProps = {
  value: BillToDraft;
  disabled?: boolean;
  sendAction?: React.ReactNode;
  onChange: (value: BillToDraft) => void;
};

export const InvoiceBillToFields: React.FC<InvoiceBillToFieldsProps> = ({
  value,
  disabled,
  sendAction,
  onChange,
}) => {
  const { t } = useTranslation(['invoices', 'common']);
  const patch = (field: Exclude<keyof BillToDraft, 'emails'>, next: string) => onChange({ ...value, [field]: next });

  return (
    <div className={catalog.form}>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToName', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.name} disabled={disabled} onChange={event => patch('name', event.target.value)} />
      </div>
      <InvoiceBillToEmails
        emails={value.emails}
        disabled={disabled}
        sendAction={sendAction}
        onChange={emails => onChange({ ...value, emails })}
      />
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToPhone', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.phone} disabled={disabled} onChange={event => patch('phone', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToAddressLine1', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.addressLine1} disabled={disabled} onChange={event => patch('addressLine1', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToAddressLine2', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.addressLine2} disabled={disabled} onChange={event => patch('addressLine2', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToCity', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.city} disabled={disabled} onChange={event => patch('city', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToStateOrProvince', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.stateOrProvince} disabled={disabled} onChange={event => patch('stateOrProvince', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToPostalCode', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.postalCode} disabled={disabled} onChange={event => patch('postalCode', event.target.value)} />
      </div>
      <div className={catalog.field}>
        <label className={catalog.label}>{t('billToCountry', { ns: 'invoices' })}</label>
        <input className={catalog.input} value={value.country} disabled={disabled} onChange={event => patch('country', event.target.value)} />
      </div>
    </div>
  );
};
